import { AmlPolicyReviewFrequency } from 'api/types/entity';
import { GridFields } from 'components';
import { getYesOrNoOptions, isYes, isNo } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { getTodayWithResetHours } from 'modules/client/common/helpers';
import {
  Field,
  useFormContext,
  maxLengthTextField,
  getAmlPolicyReviewFrequencyOptions,
  getMonetaryServiceTransactionCountForLastSixMonthsOptions,
  maxLengthName,
  maxLengthOfficerPosition,
  maxLengthAuthorityName,
} from 'modules/client/legal/forms/companyFinanceFinancialMonitoringForm';

export interface FormContentProps {
  hasBranches: boolean;
}

export const FormContent = ({ hasBranches }: FormContentProps) => {
  const { t } = useTranslation();
  const { values } = useFormContext();

  return (
    <GridFields>
      <Field.Autocomplete
        showRequiredMark
        label={t(
          'client.legal.companyFinancialMonitoring.isSubjectToRegulation.label'
        )}
        name="financeFinancialMonitoring.isSubjectToRegulation"
        optionLabelKey="label"
        options={getYesOrNoOptions(t)}
        optionValueKey="value"
        placeholder={t('common.selectPlaceholder')}
        variant="select"
      />

      {isYes(values.financeFinancialMonitoring.isSubjectToRegulation) && (
        <>
          <Field.Text
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.authorityName.label'
            )}
            maxLength={maxLengthAuthorityName}
            name="financeFinancialMonitoring.authorityName"
            placeholder={t(
              'client.legal.companyFinancialMonitoring.authorityName.placeholder'
            )}
          />
          <Field.Text
            label={t(
              'client.legal.companyFinancialMonitoring.authorityWebsite.label'
            )}
            maxLength={maxLengthTextField}
            name="financeFinancialMonitoring.authorityWebsite"
            placeholder={t(
              'client.legal.companyFinancialMonitoring.authorityWebsite.placeholder'
            )}
          />

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasAmlCftComplianceControl.label'
            )}
            name="financeFinancialMonitoring.hasAmlCftComplianceControl"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasTransactionMonitoringAndSuspiciousActivityDetection.label'
            )}
            name="financeFinancialMonitoring.hasTransactionMonitoringAndSuspiciousActivityDetection"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.amlPolicyReviewFrequency.label'
            )}
            name="financeFinancialMonitoring.amlPolicyReviewFrequency"
            optionLabelKey="label"
            options={getAmlPolicyReviewFrequencyOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {values.financeFinancialMonitoring.amlPolicyReviewFrequency?.value ===
            AmlPolicyReviewFrequency.Other && (
            <Field.Text
              showRequiredMark
              label={t(
                'client.legal.companyFinancialMonitoring.otherAmlPolicyReviewFrequency.label'
              )}
              maxLength={maxLengthTextField}
              name="financeFinancialMonitoring.otherAmlPolicyReviewFrequency"
              placeholder={t(
                'client.legal.companyFinancialMonitoring.otherAmlPolicyReviewFrequency.placeholder'
              )}
            />
          )}

          {hasBranches && (
            <Field.Autocomplete
              showRequiredMark
              label={t(
                'client.legal.companyFinancialMonitoring.amlCftPoliciesApplyToForeignEntities.label'
              )}
              name="financeFinancialMonitoring.amlCftPoliciesApplyToForeignEntities"
              optionLabelKey="label"
              options={getYesOrNoOptions(t)}
              optionValueKey="value"
              placeholder={t('common.selectPlaceholder')}
              variant="select"
            />
          )}

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasAmlCftDocumentEffectivenessAssessmentProcedures.label'
            )}
            name="financeFinancialMonitoring.hasAmlCftDocumentEffectivenessAssessmentProcedures"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {isYes(
            values.financeFinancialMonitoring
              .hasAmlCftDocumentEffectivenessAssessmentProcedures
          ) && (
            <Field.DatePicker
              label={t(
                'client.legal.companyFinancialMonitoring.dateOfLastCheck.label'
              )}
              maxDate={getTodayWithResetHours()}
              name="financeFinancialMonitoring.dateOfLastCheck"
              placeholder={t(
                'client.legal.companyFinancialMonitoring.dateOfLastCheck.placeholder'
              )}
            />
          )}

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasAmlCftInternalControlOfficer.label'
            )}
            name="financeFinancialMonitoring.hasAmlCftInternalControlOfficer"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {isYes(
            values.financeFinancialMonitoring.hasAmlCftInternalControlOfficer
          ) && (
            <>
              <Field.Text
                showRequiredMark
                label={t(
                  'client.legal.companyFinancialMonitoring.officerFirstName.label'
                )}
                maxLength={maxLengthName}
                name="financeFinancialMonitoring.officerFirstName"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerFirstName.placeholder'
                )}
                trim="start"
              />
              <Field.Text
                showRequiredMark
                label={t(
                  'client.legal.companyFinancialMonitoring.officerLastName.label'
                )}
                maxLength={maxLengthName}
                name="financeFinancialMonitoring.officerLastName"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerLastName.placeholder'
                )}
                trim="start"
              />
              <Field.Text
                label={t(
                  'client.legal.companyFinancialMonitoring.officerMiddleName.label'
                )}
                maxLength={maxLengthName}
                name="financeFinancialMonitoring.officerMiddleName"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerMiddleName.placeholder'
                )}
                trim="start"
              />
              <Field.Text
                showRequiredMark
                label={t(
                  'client.legal.companyFinancialMonitoring.officerPosition.label'
                )}
                maxLength={maxLengthOfficerPosition}
                name="financeFinancialMonitoring.officerPosition"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerPosition.placeholder'
                )}
                trim="start"
              />
              <Field.Phone
                showRequiredMark
                label={t(
                  'client.legal.companyFinancialMonitoring.officerPhoneNumber.label'
                )}
                maxLength={22}
                name="financeFinancialMonitoring.officerPhoneNumber"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerPhoneNumber.placeholder'
                )}
                type="phone"
              />
              <Field.Text
                showRequiredMark
                autoComplete="on"
                label={t(
                  'client.legal.companyFinancialMonitoring.officerEmail.label'
                )}
                name="financeFinancialMonitoring.officerEmail"
                placeholder={t(
                  'client.legal.companyFinancialMonitoring.officerEmail.placeholder'
                )}
                type="email"
              />
            </>
          )}
          <Field.Autocomplete
            label={t(
              'client.legal.companyFinancialMonitoring.canProvideAmlPolicyDocuments.label'
            )}
            name="financeFinancialMonitoring.canProvideAmlPolicyDocuments"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {isNo(
            values.financeFinancialMonitoring.canProvideAmlPolicyDocuments
          ) && (
            <Field.Text
              label={t(
                'client.legal.companyFinancialMonitoring.documentProvisionRefusalReason.label'
              )}
              maxLength={maxLengthTextField}
              name="financeFinancialMonitoring.documentProvisionRefusalReason"
              placeholder={t(
                'client.legal.companyFinancialMonitoring.documentProvisionRefusalReason.placeholder'
              )}
            />
          )}

          <Field.Autocomplete
            label={t(
              'client.legal.companyFinancialMonitoring.isInternalControlInfoConfidential.label'
            )}
            name="financeFinancialMonitoring.isInternalControlInfoConfidential"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          <Field.Autocomplete
            label={t(
              'client.legal.companyFinancialMonitoring.hadAmlCftInvestigationsOrPenalties.label'
            )}
            name="financeFinancialMonitoring.hadAmlCftInvestigationsOrPenalties"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {isYes(
            values.financeFinancialMonitoring.hadAmlCftInvestigationsOrPenalties
          ) && (
            <Field.Text
              showRequiredMark
              label={t(
                'client.legal.companyFinancialMonitoring.investigationOrPenaltyReasons.label'
              )}
              maxLength={maxLengthTextField}
              name="financeFinancialMonitoring.investigationOrPenaltyReasons"
              placeholder={t(
                'client.legal.companyFinancialMonitoring.investigationOrPenaltyReasons.placeholder'
              )}
            />
          )}

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasMandatoryControlTransactions.label'
            )}
            name="financeFinancialMonitoring.hasMandatoryControlTransactions"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          {isYes(
            values.financeFinancialMonitoring.hasMandatoryControlTransactions
          ) && (
            <Field.Text
              showRequiredMark
              label={t(
                'client.legal.companyFinancialMonitoring.mandatoryControlTransactionIdentificationProcedure.label'
              )}
              maxLength={maxLengthTextField}
              name="financeFinancialMonitoring.mandatoryControlTransactionIdentificationProcedure"
              placeholder={t(
                'client.legal.companyFinancialMonitoring.mandatoryControlTransactionIdentificationProcedure.placeholder'
              )}
            />
          )}

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.monetaryServiceTransactionCountForLastSixMonths.label'
            )}
            name="financeFinancialMonitoring.monetaryServiceTransactionCountForLastSixMonths"
            optionLabelKey="label"
            options={getMonetaryServiceTransactionCountForLastSixMonthsOptions(
              t
            )}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.isAffiliatedWithNonProfitOrganizations.label'
            )}
            name="financeFinancialMonitoring.isAffiliatedWithNonProfitOrganizations"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />
          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.receivesFundingFromStateBudget.label'
            )}
            name="financeFinancialMonitoring.receivesFundingFromStateBudget"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />
          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.receivesFundingFromForeignNonProfitNGOs.label'
            )}
            name="financeFinancialMonitoring.receivesFundingFromForeignNonProfitNGOs"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />

          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.isCompliantWithFATFRecommendations.label'
            )}
            name="financeFinancialMonitoring.isCompliantWithFATFRecommendations"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />
          <Field.Autocomplete
            showRequiredMark
            label={t(
              'client.legal.companyFinancialMonitoring.hasBeenRefusedToOpenAccountByBanks.label'
            )}
            name="financeFinancialMonitoring.hasBeenRefusedToOpenAccountByBanks"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('common.selectPlaceholder')}
            variant="select"
          />
        </>
      )}
    </GridFields>
  );
};
